import React, { useEffect, useRef } from "react";

import { formatDateOnly } from "../../../../utils/datetime";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "../../tenancies/workspaceUi";
import {
  HANDOVER_LOCATION_MAX_LENGTH,
  NOTE_MAX_LENGTH,
  TERMINATION_REASON_MAX_LENGTH,
} from "./terminationModel";

/**
 * KND-2 — the termination form.
 *
 * CONTROLLED AND DUMB. The container owns the values, the errors and the
 * request; this renders them and reports edits. No field here is computed:
 * the notice period is shown read-only as the container resolved it, and the
 * latest check-out stays EMPTY unless the admin overrides it — the server's
 * default (the last day at 10:00 Europe/Zurich) is authoritative, so the
 * client does not compute a second copy of it.
 *
 * There is no "Gekündigt durch" control: this workflow is the tenant's
 * ordinary termination and the server writes `terminated_by = tenant` itself
 * (discovery E6). There is no "Rückgabe erfolgt am" either — the physical
 * move-out is not part of a termination (E2) and stays in "Bearbeiten".
 */

// The field look of the focused deposit editor (DepositEditModal), tokens only.
const FIELD =
  "w-full rounded-[8px] border border-border bg-background px-2.5 py-1.5 " +
  "text-[13px] text-foreground focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-ring disabled:opacity-50";
const LABEL = "mb-1 block text-[12px] font-medium text-muted-foreground";
const HELP = "m-0 mt-1 text-[11.5px] leading-snug text-muted-foreground";
// The message is foreground text with a destructive MARKER, not destructive
// text: `--destructive` on the light card measures 4.37:1, under the 4.5:1 a
// sentence needs (WCAG AA). As a 2px bar it only has to reach 3:1 (non-text).
const ERROR =
  "m-0 mt-1 border-l-2 border-destructive pl-2 text-[11.5px] leading-snug text-foreground";

function Required() {
  return (
    <span aria-hidden="true" className="text-foreground">
      {" *"}
    </span>
  );
}

export default function TerminationForm({
  tenancyId,
  title,
  intro = null,
  form,
  errors = {},
  noticePeriodLabel,
  terminationMaxIso = null,
  saving = false,
  onChange,
  onSubmit,
  onCancel,
}) {
  const firstFieldRef = useRef(null);
  useEffect(() => {
    // Opening the form is the admin's own action, so focus follows it.
    firstFieldRef.current?.focus();
  }, []);

  const id = (name) => `term-${name}-${tenancyId}`;
  const set = (key) => (event) => onChange(key, event.target.value);
  const describedBy = (...ids) => ids.filter(Boolean).join(" ") || undefined;

  return (
    <form
      noValidate
      onSubmit={onSubmit}
      aria-labelledby={id("form-title")}
      data-testid={`termination-form-${tenancyId}`}
      className="flex flex-col gap-3"
    >
      <div>
        <h5
          id={id("form-title")}
          className="m-0 text-[12.5px] font-semibold text-foreground"
        >
          {title}
        </h5>
        {intro ? <p className={HELP}>{intro}</p> : null}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="min-w-0">
          <label className={LABEL} htmlFor={id("nga")}>
            Kündigung eingegangen am
            <Required />
          </label>
          <input
            ref={firstFieldRef}
            id={id("nga")}
            type="date"
            className={FIELD}
            value={form.noticeGivenAt}
            onChange={set("noticeGivenAt")}
            disabled={saving}
            required
            aria-required="true"
            aria-invalid={errors.noticeGivenAt ? "true" : undefined}
            aria-describedby={describedBy(errors.noticeGivenAt && id("nga-error"))}
          />
          {errors.noticeGivenAt ? (
            <p id={id("nga-error")} className={ERROR}>
              {errors.noticeGivenAt}
            </p>
          ) : null}
        </div>

        <div className="min-w-0">
          <label className={LABEL} htmlFor={id("te")}>
            Ende des Mietverhältnisses
            <Required />
          </label>
          <input
            id={id("te")}
            type="date"
            className={FIELD}
            value={form.terminationEffectiveDate}
            onChange={set("terminationEffectiveDate")}
            max={terminationMaxIso || undefined}
            disabled={saving}
            required
            aria-required="true"
            aria-invalid={errors.terminationEffectiveDate ? "true" : undefined}
            aria-describedby={describedBy(
              id("te-help"),
              errors.terminationEffectiveDate && id("te-error")
            )}
          />
          <p id={id("te-help")} className={HELP}>
            Letzter Miettag.
            {terminationMaxIso
              ? ` Maximal bis ${formatDateOnly(terminationMaxIso)} gemäss Vermieter-Vertrag.`
              : null}
          </p>
          {errors.terminationEffectiveDate ? (
            <p id={id("te-error")} className={ERROR}>
              {errors.terminationEffectiveDate}
            </p>
          ) : null}
        </div>

        <div className="min-w-0">
          <span id={id("npm-label")} className={LABEL}>
            Kündigungsfrist
          </span>
          <p
            aria-labelledby={id("npm-label")}
            className="m-0 py-1.5 text-[13px] text-foreground"
            data-testid={`termination-notice-period-${tenancyId}`}
          >
            {noticePeriodLabel}
          </p>
          <p className={HELP}>Aus dem Mietvertrag, hier nicht änderbar.</p>
        </div>

        <div className="min-w-0">
          <label className={LABEL} htmlFor={id("checkout")}>
            Spätestes Check-out
          </label>
          <input
            id={id("checkout")}
            type="datetime-local"
            className={FIELD}
            value={form.checkoutLocal}
            onChange={set("checkoutLocal")}
            disabled={saving}
            aria-describedby={id("checkout-help")}
          />
          <p id={id("checkout-help")} className={HELP}>
            Standard: 10:00 Uhr am letzten Miettag
          </p>
        </div>

        <div className="min-w-0">
          <label className={LABEL} htmlFor={id("handover")}>
            Abnahme/Übergabetermin
          </label>
          <input
            id={id("handover")}
            type="datetime-local"
            className={FIELD}
            value={form.handoverLocal}
            onChange={set("handoverLocal")}
            disabled={saving}
            aria-describedby={id("handover-help")}
          />
          <p id={id("handover-help")} className={HELP}>
            Nur zur Planung.
          </p>
        </div>

        <div className="min-w-0">
          <label className={LABEL} htmlFor={id("location")}>
            Übergabeort
          </label>
          <input
            id={id("location")}
            type="text"
            className={FIELD}
            value={form.handoverLocation}
            onChange={set("handoverLocation")}
            maxLength={HANDOVER_LOCATION_MAX_LENGTH}
            disabled={saving}
          />
        </div>

        <div className="min-w-0 sm:col-span-2">
          <label className={LABEL} htmlFor={id("reason")}>
            Kündigungsgrund
          </label>
          <textarea
            id={id("reason")}
            rows={2}
            className={FIELD}
            value={form.reason}
            onChange={set("reason")}
            maxLength={TERMINATION_REASON_MAX_LENGTH}
            disabled={saving}
          />
        </div>

        <div className="min-w-0 sm:col-span-2">
          <label className={LABEL} htmlFor={id("note")}>
            Notiz
          </label>
          <textarea
            id={id("note")}
            rows={2}
            className={FIELD}
            value={form.note}
            onChange={set("note")}
            maxLength={NOTE_MAX_LENGTH}
            disabled={saving}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" className={PRIMARY_BUTTON} disabled={saving}>
          {saving ? "Wird gespeichert …" : "Kündigung speichern"}
        </button>
        <button
          type="button"
          className={SECONDARY_BUTTON}
          onClick={onCancel}
          disabled={saving}
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
}
